// Circle packing — X3, how the canonicaliser packed a corpus's vocabulary.
// Corpus contains concepts; a concept contains the document terms that merged
// into it.
//
// THE ONE DEPENDENCY IN THIS DIRECTORY. `d3-hierarchy` supplies `pack()`:
// smallest-enclosing-circle plus front-chain placement, which is roughly two
// hundred lines of geometry whose failure mode is overlapping circles that
// still look plausible. Everything else here — scales, arcs, areas, the grid —
// is hand-rolled, and this is the only algorithm that earned an import. The
// package has no transitive dependencies.
//
// Nothing under `src/charts/` may be imported by `src/widget/`: the widget
// carries a hard 43KB gzip budget (`npm run widget:size`) and the dashboard is
// a separate bundle.
//
// A SINGLETON IS `member_refs.length === 1`, NOT `is_singleton`. The reconciler
// drops `is_singleton` when it builds a `CorpusEntity`, so it does not exist on
// this payload — and the name is doubly booby-trapped, because
// `Cluster.is_singleton` (a cluster of one) and `GlossaryEntry.is_singleton`
// (a term used once in its own prose) are unrelated concepts that share it.
// What this chart shades is "the canonicaliser packed nothing into this", which
// is the member count and nothing else.

import { hierarchy, pack } from "d3-hierarchy";
import { Figure } from "./Figure";
import "./charts.css";

export type PackLeaf = { key: string; label: string };
export type PackNode = {
  key: string;
  label: string;
  /** Confidence, 0..1. Exact-name clusters come back as 1.0000000000000002
   *  from float error, so callers clamp before passing it here. */
  confidence?: number | null;
  children: PackLeaf[];
};

export type PackProps = {
  label: string;
  nodes: PackNode[];
  size?: number;
  /** Called with the concept key when a circle is activated. */
  onSelect?: (key: string) => void;
  selected?: string | null;
  notMeasured?: { reason: string };
};

type Datum = { key: string; label: string; singleton: boolean; value: number };

export function Pack({
  label,
  nodes,
  size = 420,
  onSelect,
  selected,
  notMeasured,
}: PackProps) {
  const root = hierarchy<Datum & { children?: Datum[] }>(
    {
      key: "__root",
      label: "",
      singleton: false,
      value: 0,
      children: nodes.map((node) => ({
        key: node.key,
        label: node.label,
        singleton: node.children.length <= 1,
        value: 0,
        children: node.children.map((leaf) => ({
          key: leaf.key,
          label: leaf.label,
          singleton: false,
          value: 1,
        })),
      })),
    },
    (datum) => datum.children,
  ).sum((datum) => datum.value);

  const laid = pack<Datum & { children?: Datum[] }>()
    .size([size, size])
    .padding(3)(root);

  return (
    <Figure label={label} width={size} height={size} notMeasured={notMeasured}>
      {laid
        .descendants()
        .filter((node) => node.depth > 0)
        .map((node) => {
          const isConcept = node.depth === 1;
          const key = node.data.key;
          return (
            <circle
              key={key}
              className="maisight-chart-pack-node"
              data-depth={node.depth}
              data-singleton={isConcept && node.data.singleton ? "yes" : undefined}
              data-selected={isConcept && key === selected ? "yes" : undefined}
              cx={node.x}
              cy={node.y}
              r={node.r}
              // Only concepts are interactive; a leaf is one document's copy of
              // the term and has nothing of its own to show.
              onClick={isConcept && onSelect ? () => onSelect(key) : undefined}
              tabIndex={isConcept && onSelect ? 0 : undefined}
              role={isConcept && onSelect ? "button" : undefined}
              onKeyDown={
                isConcept && onSelect
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSelect(key);
                      }
                    }
                  : undefined
              }
            >
              {isConcept ? (
                <title>{`${node.data.label} — ${node.children?.length ?? 0} document(s)`}</title>
              ) : null}
            </circle>
          );
        })}
      {/* Labels only where a circle is big enough to hold one. A label that
          overflows its circle reads as belonging to the neighbour. */}
      {laid
        .descendants()
        .filter((node) => node.depth === 1 && node.r > 18)
        .map((node) => (
          <text
            key={`t-${node.data.key}`}
            className="maisight-chart-pack-label"
            x={node.x}
            y={node.y}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {node.data.label}
          </text>
        ))}
    </Figure>
  );
}
